import type { JsonValue } from "@nexus/widget-sdk";

import type { EditLayoutItem } from "./components/DashboardEditGrid";
import type { DashboardKind, DashboardRecord } from "@/lib/dashboard/types";

// Bumped when the shape of a stored composition changes (2: pages instead of views), so a
// tab never restores a draft in a shape the editor no longer reads.
const DASHBOARD_DRAFT_VERSION = 2;
const DASHBOARD_DRAFT_KEY_PREFIX = "nexus.ui.dashboardDraft";

function draftPrefix(kind: DashboardKind) {
  return kind === "template" ? "nexus.ui.templateDraft" : DASHBOARD_DRAFT_KEY_PREFIX;
}

export function getRecordDraftKey(recordId: string, kind: DashboardKind = "dashboard") {
  return `nexus.${kind}:${recordId}`;
}

export interface DashboardDraftSetup {
  completed: boolean;
  editingPageId?: string;
  reportTenantIds: number[];
  slug?: string;
  templateId?: string;
}

interface StoredDashboardDraft {
  version: typeof DASHBOARD_DRAFT_VERSION;
  updatedAt?: number;
  items: EditLayoutItem[];
  metadata?: { name: string; description: string };
  config?: DashboardRecord["config"];
  setup?: DashboardDraftSetup;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isJsonRecord(value: unknown): value is Record<string, JsonValue> {
  return isRecord(value);
}

function isEditLayoutItem(value: unknown): value is EditLayoutItem {
  if (!isRecord(value)) return false;

  return (
    typeof value.instanceId === "string" &&
    typeof value.widgetId === "string" &&
    isJsonRecord(value.config) &&
    typeof value.w === "number"
  );
}

export function getDashboardDraftKey(
  tenantIds: readonly string[],
  kind: DashboardKind = "dashboard",
): string {
  const scope = [...tenantIds].sort().join(",") || "all";
  return `${draftPrefix(kind)}:${scope}`;
}

export function readDashboardDraft(key: string): EditLayoutItem[] {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return [];

    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed) || parsed.version !== DASHBOARD_DRAFT_VERSION) return [];
    if (!Array.isArray(parsed.items) || !parsed.items.every(isEditLayoutItem)) return [];

    return parsed.items;
  } catch {
    return [];
  }
}

export function writeDashboardDraft(
  key: string,
  items: EditLayoutItem[],
  metadata?: { name: string; description: string },
  config?: DashboardRecord["config"],
  setup?: DashboardDraftSetup,
): void {
  try {
    const draft: StoredDashboardDraft = {
      version: DASHBOARD_DRAFT_VERSION,
      updatedAt: Date.now(),
      items,
      metadata,
      config,
      setup: setup ?? readDashboardDraftSetup(key) ?? undefined,
    };
    window.sessionStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // Editing remains available when browser storage is unavailable.
  }
}

export function readDashboardDraftSetup(key: string): DashboardDraftSetup | null {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(key) ?? "null");
    if (!isRecord(parsed) || parsed.version !== DASHBOARD_DRAFT_VERSION) return null;
    const setup = parsed.setup;
    if (
      isRecord(setup) &&
      typeof setup.completed === "boolean" &&
      (setup.slug === undefined || typeof setup.slug === "string") &&
      (setup.templateId === undefined || typeof setup.templateId === "string") &&
      Array.isArray(setup.reportTenantIds) &&
      setup.reportTenantIds.every((tenantId) => Number.isInteger(tenantId) && tenantId > 0)
    )
      return {
        completed: setup.completed,
        reportTenantIds: setup.reportTenantIds,
        ...(typeof setup.slug === "string" ? { slug: setup.slug } : {}),
        ...(typeof setup.templateId === "string" ? { templateId: setup.templateId } : {}),
        ...(typeof setup.editingPageId === "string" ? { editingPageId: setup.editingPageId } : {}),
      };
  } catch {
    return null;
  }
  return null;
}

export function readDashboardDraftConfig(key: string): DashboardRecord["config"] | null {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(key) ?? "null");
    if (isRecord(parsed) && parsed.version === DASHBOARD_DRAFT_VERSION && isRecord(parsed.config))
      return parsed.config as DashboardRecord["config"];
  } catch {
    /* Optional browser draft. */
  }
  return null;
}

export function readDashboardDraftTimestamp(key: string): number | null {
  try {
    const draft: unknown = JSON.parse(window.sessionStorage.getItem(key) ?? "null");
    if (
      isRecord(draft) &&
      draft.version === DASHBOARD_DRAFT_VERSION &&
      typeof draft.updatedAt === "number"
    )
      return draft.updatedAt;
  } catch {
    /* Optional browser draft. */
  }
  return null;
}

export function clearDashboardDraft(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
    window.sessionStorage.removeItem(`${key}:recordId`);
  } catch {
    // A stale draft is preferable to blocking a successful dashboard creation.
  }
}

/**
 * The local draft of a dashboard first created from "New dashboard" lives under the new-dashboard key;
 * once the record exists it is reopened by its own id, so find that key through the stored record id.
 */
export function findDashboardDraftKeyForRecord(
  recordId: string,
  kind: DashboardKind = "dashboard",
): string | null {
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (
        key.startsWith(`${draftPrefix(kind)}:`) &&
        key.endsWith(":recordId") &&
        window.sessionStorage.getItem(key) === recordId
      )
        return key.slice(0, -":recordId".length);
    }
  } catch {
    /* Optional browser draft. */
  }
  return null;
}

export function clearDashboardDraftForRecord(
  recordId: string,
  kind: DashboardKind = "dashboard",
): void {
  try {
    const keys = Object.keys(window.sessionStorage).filter(
      (key) =>
        key.startsWith(`${draftPrefix(kind)}:`) &&
        key.endsWith(":recordId") &&
        window.sessionStorage.getItem(key) === recordId,
    );
    for (const key of keys) clearDashboardDraft(key.slice(0, -":recordId".length));
    clearDashboardDraft(getRecordDraftKey(recordId, kind));
  } catch {
    return;
  }
}

export function readDashboardDraftMetadata(key: string): { name: string; description: string } {
  try {
    const draft = JSON.parse(window.sessionStorage.getItem(key) ?? "null");
    if (
      draft?.version === DASHBOARD_DRAFT_VERSION &&
      typeof draft.metadata?.name === "string" &&
      typeof draft.metadata?.description === "string"
    )
      return draft.metadata;
  } catch {
    /* Draft storage is optional. */
  }
  return { name: "Untitled dashboard", description: "" };
}
